/**
 * 2. Sign Up — tenant onboarding start; user signs up with work email to
 * begin workspace creation.
 *
 * Arabic and English (C7): the words come from the catalogs, an API refusal
 * is shown in the error catalog's copy (ErrorText), and the language switch
 * of the sign-in page is offered here too, since nobody has signed in yet to
 * have a saved language.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Field, Input, PageHeader } from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { ApiError, post } from "@/lib/api/client";
import { useTranslation } from "@/lib/i18n";
import { SIGN_UP_LIMITS } from "@/lib/i18n/errors";
import { LanguageSwitch } from "./LanguageSwitch";

/** About how long sign-up to a usable workspace takes, as the copy says. */
const SETUP_MINUTES = 2;

export function SignUpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [limited, setLimited] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLimited(false);
    setLoading(true);
    try {
      await post("/auth/signup", { email, password, displayName: name });
      // The address goes to the verification page in the navigation state,
      // for its "Send a new code" action (W2.5); never in the URL, where it
      // would reach the server's access log and the browser history.
      navigate("/verify-email", { state: { email } });
    } catch (err) {
      // Too many sign-ups or code requests for this address or from this
      // network (W2.5, W2.8a): said in sign-up's words, not sign-in's.
      if (err instanceof ApiError && err.code === "RATE_LIMITED") {
        setError(null);
        setLimited(true);
        return;
      }
      setError(err ?? new Error("Sign-up failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ inlineSize: 400, maxInlineSize: "100%" }}>
        <Card>
          <PageHeader
            title={t("auth.signup.title")}
            subtitle={t("auth.signup.subtitle", { count: SETUP_MINUTES })}
          />
          <form onSubmit={submit}>
            <Field label={t("auth.signup.name")} required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
              />
            </Field>
            <Field
              label={t("auth.signup.email")}
              required
              hint={t("auth.signup.emailHint")}
            >
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </Field>
            <Field
              label={t("auth.signup.password")}
              required
              hint={t("auth.signup.passwordHint", {
                count: SIGN_UP_LIMITS.minPassword,
              })}
            >
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={SIGN_UP_LIMITS.minPassword}
                autoComplete="new-password"
              />
            </Field>
            {limited && (
              <div
                role="alert"
                style={{
                  color: "var(--atlas-danger)",
                  fontSize: 13,
                  marginBlockEnd: 12,
                }}
              >
                {t("auth.signup.limited")}
              </div>
            )}
            {error !== null && (
              <div
                role="alert"
                style={{
                  color: "var(--atlas-danger)",
                  fontSize: 13,
                  marginBlockEnd: 12,
                }}
              >
                <ErrorText error={error} />
              </div>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              style={{ inlineSize: "100%" }}
            >
              {loading ? t("auth.signup.submitting") : t("auth.signup.submit")}
            </Button>
          </form>
          <div
            style={{
              marginBlockStart: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              fontSize: 13,
            }}
          >
            <LanguageSwitch />
            <span>
              {t("auth.signup.haveAccount")}{" "}
              <Link to="/login">{t("auth.signup.signIn")}</Link>
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
}
